import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuthProvider } from './useAuthProvider';
import { useLocation } from 'react-router-dom';
import { PageLoading } from '../components';
import LoginGate from '../components/LoginGate';
import { getSession, setSession } from '../helpers/storage';
import { ANONYMOUS_PAGEVIEW_LIMIT, DWF_PAGEVIEW_COUNT } from '../helpers/constants';

interface AuthContextType {
  user: any;
  triedEmail: string;
  loginUser: Function;
  registerUser: Function;
  logout: Function;
}

let AuthContext = createContext<AuthContextType>(null!);

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    loading,
    user,
    triedEmail,
    loginUser,
    registerUser,
    logout
  } = useAuthProvider();

  let value = {
    user,
    triedEmail,
    loginUser,
    registerUser,
    logout
  };

  if (loading) {
    return (<PageLoading />)
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  return useContext(AuthContext);
}

export const RequireAuth = ({ children }: { children: JSX.Element }) => {
  let auth = useAuth();
  let location = useLocation();
  const [pageviews, setPageviews] = useState<number>(
    () => getSession(DWF_PAGEVIEW_COUNT) || 0
  );

  // Count each page an anonymous visitor loads; after the limit is
  // reached the login/register gate below blocks further browsing.
  useEffect(() => {
    if (auth.user) return;
    const count = (getSession(DWF_PAGEVIEW_COUNT) || 0) + 1;
    setSession(DWF_PAGEVIEW_COUNT, count);
    setPageviews(count);
  }, [auth.user, location.pathname]);

  // Clicks inside the cross-origin Airtable embeds can't be observed
  // directly, but clicking into one blurs the parent window with the
  // iframe as the active element — count that as a pageview too.
  // Counts once per focus cycle: consecutive clicks inside the embed
  // register only when focus first moves into it.
  useEffect(() => {
    if (auth.user) return;
    const onBlur = () => {
      const el = document.activeElement;
      if (
        el instanceof HTMLIFrameElement &&
        el.classList.contains("airtable-embed")
      ) {
        const count = (getSession(DWF_PAGEVIEW_COUNT) || 0) + 1;
        setSession(DWF_PAGEVIEW_COUNT, count);
        setPageviews(count);
      }
    };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [auth.user]);

  if (!auth.user && pageviews > ANONYMOUS_PAGEVIEW_LIMIT) {
    return (
      <>
        {children}
        <LoginGate />
      </>
    );
  }

  return children;
}

export default AuthProvider;